/**
 * Допуски клиента (E09, ФТ-4.1–4.7).
 * Автозаявка на тяговый расчёт при регистрации; решение сотрудником.
 */

import { z } from "zod";
import { resultTierSchema } from "./attributes";
import { apiSuccessSchema } from "./envelope";

export const accessTypeSchema = z.enum([
  "price",
  "bom",
  "traction_calc",
  "sizing",
  "reference",
  "media_3d",
]);
export type AccessType = z.infer<typeof accessTypeSchema>;

export const accessGrantStatusSchema = z.enum(["pending", "active", "revoked"]);
export type AccessGrantStatus = z.infer<typeof accessGrantStatusSchema>;

export const accessGrantSchema = z.object({
  id: z.string().uuid(),
  clientId: z.string().uuid(),
  accessType: accessTypeSchema,
  status: accessGrantStatusSchema,
  tier: resultTierSchema.nullable(),
  requestedAt: z.string().datetime(),
  decidedAt: z.string().datetime().nullable(),
  personalCode: z.string().length(8),
  company: z.string().min(1),
  fullName: z.string().min(1),
});
export type AccessGrant = z.infer<typeof accessGrantSchema>;

export const accessGrantListResponseSchema = apiSuccessSchema(z.array(accessGrantSchema));

export const decideAccessGrantRequestSchema = z.object({
  decision: z.enum(["approve", "deny"]),
});
export type DecideAccessGrantRequest = z.infer<typeof decideAccessGrantRequestSchema>;

/** Создание заявки сотрудником (остальные виды допуска после traction_calc). */
export const createAccessGrantRequestSchema = z.object({
  clientId: z.string().uuid(),
  accessType: accessTypeSchema,
  /** Для traction_calc обычно full; для price/bom и др. — null. */
  tier: resultTierSchema.nullable().optional(),
});
export type CreateAccessGrantRequest = z.infer<typeof createAccessGrantRequestSchema>;

/** Клиентская заявка на допуск из wizard (по проекту с привязанным клиентом). */
export const publicAccessGrantRequestSchema = z.object({
  accessType: accessTypeSchema,
});
export type PublicAccessGrantRequest = z.infer<typeof publicAccessGrantRequestSchema>;

/** Заявка из кабинета по персональному коду. */
export const clientCabinetAccessGrantRequestSchema = z.object({
  personalCode: z.string().trim().length(8),
  accessType: accessTypeSchema,
});
export type ClientCabinetAccessGrantRequest = z.infer<typeof clientCabinetAccessGrantRequestSchema>;

export const publicAccessGrantRequestResultSchema = z.object({
  id: z.string().uuid(),
  accessType: accessTypeSchema,
  status: accessGrantStatusSchema,
  /** Уже была pending/active заявка — повторно не создаём. */
  alreadyOpen: z.boolean(),
});
export type PublicAccessGrantRequestResult = z.infer<typeof publicAccessGrantRequestResultSchema>;

export const publicAccessGrantRequestResultResponseSchema = apiSuccessSchema(
  publicAccessGrantRequestResultSchema,
);

export const ACCESS_GRANT_ERROR_CODES = {
  NOT_FOUND: "ACCESS_GRANT_NOT_FOUND",
  NOT_PENDING: "ACCESS_GRANT_NOT_PENDING",
  CLIENT_NOT_FOUND: "ACCESS_GRANT_CLIENT_NOT_FOUND",
  DUPLICATE_PENDING: "ACCESS_GRANT_DUPLICATE_PENDING",
  PROJECT_NOT_FOUND: "ACCESS_GRANT_PROJECT_NOT_FOUND",
  NO_CLIENT: "ACCESS_GRANT_NO_CLIENT",
} as const;
export type AccessGrantErrorCode =
  (typeof ACCESS_GRANT_ERROR_CODES)[keyof typeof ACCESS_GRANT_ERROR_CODES];
